import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { useAuth } from '../context/AuthContext';
import ScrollHeroVisual from '../components/landing/ScrollHeroVisual';
import LandingRoleCard, { RoleCardLayout } from '../components/landing/LandingRoleCard';
import { RoleIllustrationType } from '../components/landing/RoleCardIllustration';
import { useScrollProgress } from '../hooks/useScrollProgress';
import { useInViewSection } from '../hooks/useInViewSection';
import SeoHead from '../components/seo/SeoHead';
import { getLandingStructuredData, LANDING_SEO } from '../components/seo/landingStructuredData';
import { FAQ_ITEMS } from '../config/site';
import ArtistCommunitySection from '../components/landing/ArtistCommunitySection';
import './Landing.css';

const ROLE_CARDS: {
  key: 'rapper' | 'producer' | 'listener';
  role: RoleIllustrationType;
  title: string;
  tagline: string;
  accentClass: string;
  layout: RoleCardLayout;
  features: { text: string }[];
}[] = [
  {
    key: 'rapper',
    role: 'rapper',
    title: 'For Rappers',
    tagline: 'Drop bars',
    accentClass: 'landing-role-card--rapper',
    layout: 'visual-right',
    features: [
      { text: 'Find fresh beats from producers across India' },
      { text: 'Record and share verses right in your browser' },
      { text: 'Collaborate and build your portfolio' },
    ],
  },
  {
    key: 'producer',
    role: 'producer',
    title: 'For Producers',
    tagline: 'Flip beats',
    accentClass: 'landing-role-card--producer',
    layout: 'visual-left',
    features: [
      { text: 'Upload beats and set how many verses you want' },
      { text: 'Get recordings from rappers on your tracks' },
      { text: 'Grow your network state by state' },
    ],
  },
  {
    key: 'listener',
    role: 'listener',
    title: 'For Listeners',
    tagline: 'Feel the vibe',
    accentClass: 'landing-role-card--listener',
    layout: 'visual-right',
    features: [
      { text: 'Explore new Desi Hip-Hop every day' },
      { text: 'Vote for tracks you love' },
      { text: 'Support rising talent across the scene' },
    ],
  },
];

const Landing: React.FC = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const scrollProgress = useScrollProgress();

  const heroRef = useRef<HTMLElement>(null);
  const rapperRef = useRef<HTMLElement>(null);
  const producerRef = useRef<HTMLElement>(null);
  const listenerRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);

  const sectionRefs = {
    hero: heroRef,
    rapper: rapperRef,
    producer: producerRef,
    listener: listenerRef,
    cta: ctaRef,
  };

  const phase = useInViewSection(sectionRefs);
  const blobOffset = scrollProgress * 120;

  const refMap = { rapper: rapperRef, producer: producerRef, listener: listenerRef };

  // Redirect logged-in users to the home feed (preserve original landing behavior)
  useEffect(() => {
    if (!authState.loading && authState.user) {
      navigate('/home', { replace: true });
    }
  }, [authState.user, authState.loading, navigate]);

  return (
    <div className="landing-page">
      <SeoHead
        title={LANDING_SEO.title}
        description={LANDING_SEO.description}
        path={LANDING_SEO.path}
        keywords={LANDING_SEO.keywords}
        jsonLd={getLandingStructuredData()}
      />

      <div className="landing-preview__bg" aria-hidden>
        <div
          className="landing-preview__blob landing-preview__blob--1"
          style={{ transform: `translate(${blobOffset * 0.15}px, ${blobOffset * 0.3}px)` }}
        />
        <div
          className="landing-preview__blob landing-preview__blob--2"
          style={{ transform: `translate(${-blobOffset * 0.2}px, ${blobOffset * 0.25}px)` }}
        />
        <div className="landing-preview__grid" />
      </div>

      <div className="landing-preview__hero-layout max-w-7xl mx-auto container-padding">
        <section ref={heroRef} className="landing-preview__hero">
          <p className="landing-preview__eyebrow">Desi Hip-Hop · India</p>
          <h1 className="landing-preview__headline">
            <span className="landing-preview__brand">DHH Collab</span>
          </h1>
          <h3 className="landing-preview__headline-sub">Where beats meet bars</h3>
          <p className="landing-preview__subhead">
            A creative playground for producers, rappers, and fans of Indian Hip-Hop. Upload beats,
            drop verses, and let the community decide what hits. Whether you&apos;re making music or
            just vibing to it, this is your space to discover, collaborate, and grow.
          </p>
          <div className="landing-preview__cta-row">
            <Link to="/signup">
              <Button variant="primary" size="lg" className="landing-preview__cta-primary">
                Get Started
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="landing-preview__cta-sign-in">
                Sign In
              </Button>
            </Link>
          </div>
          <p className="landing-preview__scroll-hint lg:hidden">
            Scroll to explore
            <span className="landing-preview__scroll-arrow" aria-hidden>
              ↓
            </span>
          </p>
          <div className="landing-preview__hero-visual-mobile lg:hidden">
            <ScrollHeroVisual phase={phase} scrollProgress={scrollProgress} />
          </div>
        </section>

        <aside className="landing-preview__visual-column hidden lg:block">
          <div className="landing-preview__sticky-visual">
            <ScrollHeroVisual phase={phase} scrollProgress={scrollProgress} />
          </div>
        </aside>
      </div>

      <section className="landing-preview__features-wide">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="landing-preview__features-header">
            <h2>Join the movement</h2>
            <p>
              Whether you&apos;re making music or just vibing to it, this is your space to
              discover, collaborate, and grow.
            </p>
          </div>

          <div className="landing-preview__role-grid">
            {ROLE_CARDS.map((card, i) => (
              <section
                key={card.key}
                ref={refMap[card.key]}
                className="landing-preview__role-section"
              >
                <LandingRoleCard
                  role={card.role}
                  title={card.title}
                  tagline={card.tagline}
                  accentClass={card.accentClass}
                  features={card.features}
                  layout={card.layout}
                  index={i}
                />
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-paths" aria-labelledby="landing-paths-heading">
        <div className="max-w-7xl mx-auto container-padding">
          <header className="landing-paths__head">
            <h2 id="landing-paths-heading">Two ways a beat becomes a track</h2>
            <p>The difference is who decides it goes public.</p>
          </header>
          <div className="landing-paths__grid">
            <article className="landing-path landing-path--cypher">
              <span className="landing-path__kicker">Open booth</span>
              <h3 className="landing-path__title">Cypher</h3>
              <p className="landing-path__line">
                One beat. Many rappers. Every take goes live as you record.
              </p>
              <ol className="landing-path__steps">
                <li>Producer posts a cypher beat</li>
                <li>Any rapper records in the browser</li>
                <li>The verse is public on the spot</li>
              </ol>
            </article>
            <article className="landing-path landing-path--collab">
              <span className="landing-path__kicker">One mix</span>
              <h3 className="landing-path__title">Collab</h3>
              <p className="landing-path__line">
                One beat. One release. The producer picks the verse the world hears.
              </p>
              <ol className="landing-path__steps">
                <li>Producer posts a collab beat</li>
                <li>Rappers send private takes</li>
                <li>One mix is published. Recording closes.</li>
              </ol>
            </article>
          </div>
        </div>
      </section>

      <ArtistCommunitySection />

      <section className="landing-preview__faq max-w-4xl mx-auto container-padding" aria-labelledby="landing-faq-heading">
        <h2 id="landing-faq-heading" className="landing-preview__faq-title">
          About DHH &amp; DHH Collab
        </h2>
        <p className="landing-preview__faq-lead">
          Answers for artists, listeners, and anyone searching for Desi Hip-Hop collaboration in India.
        </p>
        <dl className="landing-preview__faq-list">
          {FAQ_ITEMS.map(item => (
            <div key={item.question} className="landing-preview__faq-item">
              <dt>{item.question}</dt>
              <dd>{item.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section
        ref={ctaRef}
        className="landing-preview__cta-section max-w-7xl mx-auto container-padding"
      >
        <div className="landing-preview__cta-card">
          <h2>Ready to join the movement?</h2>
          <p>Create your account and start collaborating today.</p>
          <Link to="/signup">
            <Button variant="primary" size="lg" className="landing-preview__cta-primary">
              Create Your Account
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;
